import { isDesktopShell } from "@/lib/platform";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const PLAYER_WINDOW_NAME = "omnimedia-player";

/** Handle to the player tab we opened (web only), so we can close it again. */
let openedPlayerWindow: Window | null = null;

function normalizePath(path: string): string {
  return path.startsWith("/") ? path : `/${path}`;
}

/** Absolute URL for the dedicated player tab/window. */
export function playerSurfaceUrl(path = "/player"): string {
  const normalized = normalizePath(path);
  const [pathname, search = ""] = normalized.split("?");
  const params = new URLSearchParams(search);
  params.set("surface", "player");
  const query = params.toString();
  return `${window.location.origin}${basePath}${pathname}?${query}`;
}

/**
 * Open or focus the player in a separate tab (web) or window (desktop).
 *
 * `credential` is the durable screen token just minted by claiming the device.
 * On the desktop shell the player window lives on a DIFFERENT origin (the
 * offline loopback bundle) from this control hub (the remote omnimedia.app),
 * so localStorage can no longer bridge the two — the token rides the IPC call
 * and the shell injects it into the player window. On web the player tab shares
 * this origin, so it reads the token the caller already persisted; the argument
 * is ignored there.
 */
export function openPlayerSurface(
  path = "/player",
  credential?: string,
  deviceId?: string,
  mode?: "players" | "both",
): void {
  const url = playerSurfaceUrl(path);
  if (isDesktopShell()) {
    // deviceId + mode let the shell persist WHICH device this box runs and
    // WHICH operator window (companion vs control) to restore on relaunch.
    void window.omnimediaDesktop?.openPlayer(url, credential, deviceId, mode);
    return;
  }
  const opened = window.open(url, PLAYER_WINDOW_NAME);
  if (opened) {
    opened.opener = window;
    openedPlayerWindow = opened;
  }
}

/**
 * Close the separate player tab/window — used when the operating window leaves
 * the session (logout / switch mode / switch workspace) so a Player-and-Control
 * setup collapses back to a single window. No-op from the player surface itself.
 */
export function closePlayerSurface(): void {
  if (isPlayerSurface()) return;
  if (isDesktopShell()) {
    void window.omnimediaDesktop?.closePlayer?.();
    return;
  }
  try {
    openedPlayerWindow?.close();
  } catch {
    // Cross-origin / already-closed — best effort.
  }
  openedPlayerWindow = null;
}

/** True when this browsing context is the dedicated player surface. */
export function isPlayerSurface(): boolean {
  if (typeof window === "undefined") return false;
  if (window.name === PLAYER_WINDOW_NAME) return true;
  return new URLSearchParams(window.location.search).get("surface") === "player";
}
